import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  TextField,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Celebration as CelebrationIcon,
  Check as CheckIcon,
  Login as LoginIcon,
  Assessment as AssessmentIcon,
  QuestionAnswer as SurveyIcon,
  Insights as InsightIcon,
  AccessTime as AccessTimeIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { DemoProvisioningResult } from '../types';

interface DemoSuccessModalProps {
  open: boolean;
  onClose: () => void;
  demoResult: DemoProvisioningResult | null;
}

/**
 * Calculate time remaining until expiration
 */
function calculateTimeRemaining(expiresAt: string): {
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
} {
  const now = new Date().getTime();
  const expiry = new Date(expiresAt).getTime();
  const diff = expiry - now;

  if (diff <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, expired: true };
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { hours, minutes, seconds, expired: false };
}

/**
 * Format time remaining as string
 */
function formatTimeRemaining(time: { hours: number; minutes: number; seconds: number }): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(time.hours)}:${pad(time.minutes)}:${pad(time.seconds)}`;
}

/**
 * Demo Success Modal
 *
 * Displays confirmation and access information after successful demo provisioning.
 * Features:
 * - Email confirmation message (credentials sent via email)
 * - Countdown timer for demo expiration
 * - Direct link to login
 * - What's included in the demo overview
 */
export const DemoSuccessModal: React.FC<DemoSuccessModalProps> = ({
  open,
  onClose,
  demoResult,
}) => {
  const [timeRemaining, setTimeRemaining] = useState<ReturnType<typeof calculateTimeRemaining>>({
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
  });
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Update countdown timer every second
  useEffect(() => {
    if (!open || !demoResult?.expiresAt) return;

    const updateTimer = () => {
      setTimeRemaining(calculateTimeRemaining(demoResult.expiresAt));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [open, demoResult?.expiresAt]);

  const handleGoToLogin = useCallback(() => {
    if (demoResult?.accessUrl) {
      window.open(demoResult.accessUrl, '_blank', 'noopener,noreferrer');
    }
  }, [demoResult?.accessUrl]);

  const handleCopyPassword = () => {
    setPasswordCopied(true);
  };

  const handleSnackbarClose = () => {
    setPasswordCopied(false);
  };

  if (!demoResult) return null;

  const features = [
    {
      icon: <SurveyIcon color="primary" />,
      primary: '2 pre-built surveys with responses',
      secondary: 'Student Feedback and Course Evaluation surveys with realistic data',
    },
    {
      icon: <AssessmentIcon color="primary" />,
      primary: 'Sample reports and insights',
      secondary: 'See how survey results are visualized and analyzed',
    },
    {
      icon: <InsightIcon color="primary" />,
      primary: 'Full lecturer access',
      secondary: 'Create surveys, manage instances, and view detailed reports',
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CelebrationIcon sx={{ color: 'success.main', fontSize: 32 }} />
          <Typography variant="h5" component="span" fontWeight={600}>
            Your Demo is Ready!
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Welcome message */}
        <Typography variant="body1" sx={{ mb: 3 }}>
          Welcome, <strong>{demoResult.email?.split('@')[0] ?? demoResult.email}</strong>! Your demo
          environment has been created and is ready to explore.
        </Typography>

        {/* Email confirmation section */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            bgcolor: 'success.50',
            borderColor: 'success.200',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <CheckIcon sx={{ color: 'success.main', fontSize: 24, mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Check Your Email
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                We&apos;ve sent your login credentials to{' '}
                <Typography component="span" variant="body2" fontWeight={600}>
                  {demoResult.email}
                </Typography>
                . Please check your inbox for the temporary password.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Tip: If you don&apos;t see the email, check your spam folder
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Password display section */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            bgcolor: 'primary.50',
            borderColor: 'primary.200',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Your Temporary Password
          </Typography>
          <TextField
            fullWidth
            value={demoResult.temporaryPassword}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '0.95rem' },
              endAdornment: (
                <InputAdornment position="end">
                  <CopyToClipboard text={demoResult.temporaryPassword} onCopy={handleCopyPassword}>
                    <IconButton edge="end" size="small" color="primary">
                      <CopyIcon />
                    </IconButton>
                  </CopyToClipboard>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Use this password to log in for the first time
          </Typography>
        </Paper>

        {/* Countdown timer */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: timeRemaining.expired ? 'error.50' : 'info.50',
            borderColor: timeRemaining.expired ? 'error.200' : 'info.200',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon
              color={timeRemaining.expired ? 'error' : 'info'}
              sx={{ fontSize: 20 }}
            />
            <Typography variant="body2" color="text.secondary">
              {timeRemaining.expired ? (
                'Your demo has expired'
              ) : (
                <>
                  Your demo expires in:{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight={600}
                    fontFamily="monospace"
                    color={timeRemaining.hours < 1 ? 'error.main' : 'text.primary'}
                  >
                    {formatTimeRemaining(timeRemaining)}
                  </Typography>
                </>
              )}
            </Typography>
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* What's included */}
        <Typography variant="subtitle2" gutterBottom>
          What&apos;s included in your demo:
        </Typography>
        <List dense disablePadding>
          {features.map((feature, index) => (
            <ListItem key={index} sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>{feature.icon}</ListItemIcon>
              <ListItemText
                primary={feature.primary}
                secondary={feature.secondary}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={handleGoToLogin}
          disabled={timeRemaining.expired}
          sx={{
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            '&:hover': {
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
            },
          }}
        >
          Go to Login
        </Button>
      </DialogActions>

      <Snackbar
        open={passwordCopied}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Password copied to clipboard!
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
